import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  inject,
  input,
  model,
  output,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { mgcIconSearchRegular } from '@sb/mgc-icons';
import { debounceTime } from 'rxjs';

import { EmptyStateComponent } from '../feedback/empty-state/empty-state.component';
import { IconRegistry } from '../icon';
import { ScrollableDirective } from '../scrollable/scrollable.directive';
import { SearchInputComponent } from '../search-input';
import { TableHeaderSortComponent } from './table-header-sort.component';
import { SB_TABLE } from './table-token';
import { TableCellDirective } from './table.directives';
import { defaultTableSortDirection, tableFilterBy, tableFlipDirection, tableSortBy } from './table.helpers';
import { TableColumn, TableSortDirection, TableSortEvent } from './table.model';

@Component({
  selector: 'sb-table',
  standalone: true,
  exportAs: 'sbTable',
  imports: [
    NgTemplateOutlet,
    ScrollableDirective,
    EmptyStateComponent,
    ReactiveFormsModule,
    SearchInputComponent,
    NgClass,
    TableHeaderSortComponent,
    TableCellDirective,
    TranslateModule,
  ],
  // encapsulation must be none to allow for styling of template content
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './table.component.html',
  providers: [{ provide: SB_TABLE, useExisting: TableComponent }],
  styleUrls: ['./table.component.scss'],
})
export class TableComponent<T extends Record<string, any>> {
  readonly searchDebounceTime = 300;
  // data display
  readonly columns = input<TableColumn<T>[]>();
  readonly dataSet = input.required<T[]>();
  readonly dataKey = input.required<keyof T>();
  readonly title = input<string | SafeHtml>();

  //tempates
  readonly rowTemplate = contentChild<TemplateRef<{ $implicit: T }>>('row');
  readonly headerRowTemplate = contentChild<TemplateRef<{ columns: TableColumn<T>[] }>>('headerRow');
  readonly footerRowTemplate = contentChild<TemplateRef<void>>('footerRow');

  // sorting
  readonly sorted = output<TableSortEvent<T>>();
  readonly sortKey = model<keyof T>();
  readonly sortDirection = model<TableSortDirection>(defaultTableSortDirection);

  //filtering
  readonly searchable = input<boolean>(false);
  readonly searchControl = new FormControl('');
  private readonly searchFor = toSignal(this.searchControl.valueChanges.pipe(debounceTime(this.searchDebounceTime)), {
    initialValue: '',
  });

  // stickiness
  readonly stickyHeader = input<boolean>();
  readonly stickyFooter = input<boolean>();

  readonly displayData = computed(() => {
    const sortKey = this.sortKey() || '';
    const sortDirection = this.sortDirection();
    const searchable = this.searchable();
    const data = this.dataSet();
    const search = this.searchFor() || '';

    // check if the data should be filtered
    const result = searchable ? tableFilterBy(data, search, this.columns()) : data;

    // check if the data should be sorted
    return sortKey && sortDirection ? tableSortBy(result, sortKey, sortDirection) : result;
  });

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconSearchRegular]);
  }

  public changeSort(key: keyof T): void {
    const sortKey = this.sortKey();
    const sortDirection = this.sortDirection();
    const direction = sortKey === key ? tableFlipDirection(sortDirection) : sortDirection;
    this.sortKey.set(key);
    this.sortDirection.set(direction);
    this.sorted.emit({ active: key, direction });
  }
}
